import { useEffect, useState } from "react";
import { X, Loader2, UsersRound } from "lucide-react";
import api, { formatError } from "@/lib/api";

/**
 * CreateGroupModal — single-purpose modal that creates a new group and
 * makes the current user its Owner. Used from the AppShell nav button
 * and from the first-time empty-state prompt on the Dashboard.
 *
 * On success calls onCreated(group) with the new group document so the
 * caller can navigate or refresh state.
 */
export default function CreateGroupModal({ open, onClose, onCreated, title = "Create a group" }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) { setName(""); setDescription(""); setErr(""); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/groups", { name: name.trim(), description: description.trim() });
      onCreated && onCreated(data);
      onClose && onClose();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="create-group-modal"
    >
      <form
        onSubmit={submit}
        className="bd-surface w-full max-w-md flex flex-col gap-4"
        style={{ padding: "1.4rem 1.4rem 1.2rem" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UsersRound size={18} style={{ color: "var(--primary)" }} />
            <h2 className="text-lg font-extrabold" style={{ letterSpacing: "-0.02em" }}>{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem 0.5rem" }}
                  data-testid="create-group-close">
            <X size={14} />
          </button>
        </div>

        <p className="text-sm" style={{ color: "var(--text-dim)" }}>
          A group is the container that holds your members and your events. You&apos;ll be the <strong>originator</strong>.
        </p>

        <label className="bd-label">
          Group name
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. The Smiths, Office Crew, Tennis Tuesdays"
            className="bd-input"
            maxLength={80}
            data-testid="create-group-name"
          />
        </label>

        <label className="bd-label">
          Short description <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What's this group for?"
            className="bd-input"
            maxLength={300}
            data-testid="create-group-description"
          />
        </label>

        {err && (
          <div className="text-sm" style={{ color: "#ff3b30" }} data-testid="create-group-error">{err}</div>
        )}

        <div className="flex items-center justify-end gap-2 mt-1">
          <button type="button" onClick={onClose} className="bd-btn bd-btn-ghost" data-testid="create-group-cancel">
            Cancel
          </button>
          <button
            type="submit"
            className="bd-btn"
            style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
            disabled={busy || !name.trim()}
            data-testid="create-group-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <UsersRound size={14} />}
            <span style={{ marginLeft: 6 }}>Create group</span>
          </button>
        </div>
      </form>
    </div>
  );
}
